import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { reportError } from '../lib/observability';
import { reportClientError } from '../lib/error-reporter';
import { isStaleChunkError, maybeReloadForStaleChunk, maybeReloadForStaleBuild } from '../lib/chunkReload';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level React error boundary. Catches render-time crashes anywhere below it,
 * reports them to OpenObserve (a no-op if client telemetry is disabled), and shows
 * a minimal on-brand fallback with a Reload button instead of a blank white screen.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Catch-all for a stale lazy chunk after a deploy (e.g. an import path not
    // wrapped in importChunk): self-heal with one loop-guarded reload instead of
    // stranding the user on the fallback. maybeReloadForStaleChunk navigates away
    // when it reloads; if it's already reloaded recently it returns false and we
    // fall through to reporting + the fallback (no loop). See lib/chunkReload.ts.
    if (isStaleChunkError(error) && maybeReloadForStaleChunk()) return;
    // OpenObserve/OTLP (no-op if telemetry off) + the always-on server store.
    reportError(error, { componentStack: info.componentStack });
    reportClientError(error, { kind: 'error-boundary', componentStack: info.componentStack ?? undefined });
    // Report FIRST, then consider healing: a crash on a build the server no
    // longer serves is deploy skew, not a bug in the running code, and one
    // reload cures it — which is what six users' worth of b-01795 crashes each
    // needed a manual click for. The report still lands because the reporter
    // sends via sendBeacon, which survives the unload, so Diagnostics keeps
    // seeing skew crashes rather than going quiet about them.
    //
    // maybeReloadForStaleBuild only reloads when the running build is provably
    // NOT the deployed one (and at most once per 20s / 3 per session, a budget
    // it shares with the chunk heal). A crash on the CURRENT build resolves
    // false and falls through to the fallback below, exactly as before — a
    // deterministic bug must never be hidden behind a reload loop. (b-01797)
    // The .catch is belt-and-braces: an unhandled rejection HERE would be caught
    // by the global reporter and reported as a fresh error, i.e. a crash handler
    // that manufactures crashes.
    void maybeReloadForStaleBuild().catch(() => { /* heal is best-effort */ });
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-6)',
          background: 'var(--bg-primary)',
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: '100%',
            textAlign: 'center',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-8) var(--space-6)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)',
            }}
          >
            Something went wrong
          </div>
          <div
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
              lineHeight: 'var(--leading-normal)',
              marginBottom: 'var(--space-6)',
            }}
          >
            The app hit an unexpected error. Reloading usually clears it.
          </div>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
